// Client-only wrapper around the SEON Web SDK (@seontechnologies/seon-javascript-sdk).
// All methods are no-ops on the server (SSG prerender) and swallow errors —
// fraud telemetry must never break the borrower UI.

// Bind to the SDK's own shipped types (agent.d.ts) so this wrapper can't drift from the
// real init/getSession signatures. `typeof import(...)` is type-only — no runtime import.
type SeonSdk = typeof import('@seontechnologies/seon-javascript-sdk').default

const DEFAULT_TARGETS = 'input, select, textarea'

let behavioralInitialized = false
// The SDK is a singleton. Cache the dynamic import so the init state set by
// initBehavioralCollection() is the same instance later read by getSession().
let sdkPromise: Promise<SeonSdk> | null = null

function isClient(): boolean {
  return typeof window !== 'undefined'
}

function loadSdk(): Promise<SeonSdk> {
  if (!sdkPromise) {
    sdkPromise = import('@seontechnologies/seon-javascript-sdk')
      .then(mod => ((mod as { default?: SeonSdk }).default ?? mod) as SeonSdk)
      .catch((e) => {
        // Don't cache a rejected import (CSP, chunk 404, transient network) — a later
        // getSession() must be able to retry rather than reuse the dead promise.
        sdkPromise = null
        throw e
      })
  }
  return sdkPromise
}

function messageOf(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined
}

export function useSeon() {
  /**
   * Start device fingerprinting + behavioral monitoring. Targets must already
   * exist in the DOM. Safe to call repeatedly; subsequent calls are ignored.
   */
  async function initBehavioralCollection(targets = DEFAULT_TARGETS): Promise<void> {
    if (!isClient() || behavioralInitialized) {
      return
    }
    try {
      const seon = await loadSdk()
      seon.init({ behavioralDataCollection: { targets } })
      behavioralInitialized = true
    } catch (e) {
      // @ts-expect-error - log is globally declared
      log?.warning('[CURE][SEON] init failed', messageOf(e))
    }
  }

  /**
   * Produce the encrypted session payload. Returns null if the SDK is
   * unavailable or errors — callers must treat a missing session as acceptable.
   */
  async function getSession(): Promise<string | null> {
    if (!isClient()) {
      return null
    }
    try {
      const seon = await loadSdk()
      return await seon.getSession({ fieldTimeoutMs: 5000 })
    } catch (e) {
      // @ts-expect-error - log is globally declared
      log?.warning('[CURE][SEON] getSession failed', messageOf(e))
      return null
    }
  }

  return { initBehavioralCollection, getSession }
}
